import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { colors } from "@zapalacorp/tokens";

import { ThemeProvider, Theme, StyledEngineProvider } from "@mui/material/styles";
import { StylesProvider, createGenerateClassName } from "@mui/styles";
import CreateCrewModal from "../components/Diary/popups/CreateCrew";
import StorageBoxSingleUseModal from "Components/Generic/StorageBoxSingleUseModal";
import { useEmitter } from "Hooks/useEventBus";
import { DespatchCrew } from "Models/despatch.crew";
import { TaskComment } from "Models/task.comment";
import { useTranslation } from "react-i18next";
import { BusEventNames } from "Util/enums";
import { Information as InformationIcon } from "iconsax-react";

import { dispatchTheme } from "../../../container/themes/dispatchTheme";
import { DiaryType } from "../../../components/DiaryCalendar";
import UITabMaterial, { PanelProps } from "../../../components/Generic/UITabMaterial";
import TitlePage from "../../../components/TitlePage";
import { DespatchCancelTask } from "../components/Diary/popups/CancelTask";
import { ChangeTimeModal } from "../components/Diary/popups/ChangeTaskTime/ChangeTimeModal";
import { DespatchCreateTask } from "../components/Tasks/popups/CreateTask";
import { DespatchCreatePlaceholderTask } from "../components/Tasks/popups/CreateTask/DespatchCreatePlaceholderTask";
import { DespatchDeletePlaceholderTask } from "../components/Diary/popups/DeletePlaceholderTask";
import { DespatchTaskCommentsModal } from "../components/Diary/popups/TaskComments";
import { TimeControlAndExpenseModal } from "Components/TimeControlModal";
import { DiarySeeTasks } from "../components/Diary/SeeTasks";
import { DespatchFilters } from "../components/Diary/shared/DespatchFilters";
import useStyles, {
  DispatchInformation,
  DispatchInformationTooltip,
  DispatchInfoTimezoneWrapper,
} from "../components/Diary/styles";
import { useDespatchPopupHandlers } from "../components/Diary/useDespatchPopupHandlers";
import {
  DiaryListView,
  DiaryMonthView,
  DiaryResourcesView,
  DiaryWeekView,
} from "../components/Diary/views";
import {
  OperationDespatchProvider,
  useOperationDespatchContext,
} from "../contexts/DespatchContext";
import { useAppState } from "Context/StateContext";
import { FN_NAMES, hasPermission, isShowAction } from "Util/permissions";
import { IconTimezoneClock } from "Components/Generic/Icons/IconTimezoneClock";
import { formatGMT } from "Util/Format";

const generateClassName = createGenerateClassName({
  productionPrefix: "opd-",
  seed: "operations-dispatch",
});

const DiaryComp: React.FC = () => {
  const classes = useStyles();

  const {
    setViewMode,
    viewMode,
    fullscreenContainerRef,
    createTaskRef,
    createPlaceholderTaskRef,
    deletePlaceholderTaskRef,
    despatchTimeControlRef,
    cancelTaskRef,
    changeTimeRef,
    taskCommentRef,
    seeTasksModalRef,
    storageBoxSingleUseRef,
    createCrewModalRef,
  } = useOperationDespatchContext();
  const { t: translate } = useTranslation();

  const { emit } = useEmitter();
  const { currentUser, userPermissions, userTimezone } = useAppState();
  const [dispatchPermission, setDispatchPermission] = useState<{
    view: {
      day: boolean;
      week: boolean;
      month: boolean;
      list: boolean;
    };
    filter: boolean;
    dataSet: {
      members: boolean;
      teams: boolean;
      tasks: boolean;
      equipments: boolean;
      storageBoxes: boolean;
    };
  }>({
    view: {
      day: false,
      week: false,
      month: false,
      list: false,
    },
    filter: false,
    dataSet: {
      members: false,
      teams: false,
      tasks: false,
      equipments: false,
      storageBoxes: false,
    },
  });

  const formatTimezoneDisplay = (timezone: any) => {
    if (!timezone?.gmtOffset || !timezone?.nameFormatted) {
      return `${timezone?.name || "Unknown"} (GMT ${timezone?.gmtOffset || 0})`;
    }

    return `GMT (${formatGMT(timezone.gmtOffset)}) ${timezone.nameFormatted
      ?.replace("/", " / ")
      .replace("_", "")}`;
  };

  useEffect(() => {
    if (userPermissions && userPermissions?.length > 0) {
      setDispatchPermission({
        view: {
          day: hasPermission(userPermissions, FN_NAMES.FUNCTION_OPERATIONS_DISPATCH_VIEW_DAY),
          week: hasPermission(userPermissions, FN_NAMES.FUNCTION_OPERATIONS_DISPATCH_VIEW_WEEK),
          month: hasPermission(userPermissions, FN_NAMES.FUNCTION_OPERATIONS_DISPATCH_VIEW_MONTH),
          list: hasPermission(userPermissions, FN_NAMES.FUNCTION_OPERATIONS_DISPATCH_VIEW_LIST),
        },
        filter: hasPermission(userPermissions, FN_NAMES.FUNCTION_OPERATIONS_DISPATCH_FILTER),
        dataSet: {
          members: hasPermission(userPermissions, FN_NAMES.FUNCTION_OPERATIONS_DISPATCH_MEMBERS),
          teams: hasPermission(userPermissions, FN_NAMES.FUNCTION_OPERATIONS_DISPATCH_TEAMS),
          tasks: hasPermission(userPermissions, FN_NAMES.FUNCTION_OPERATIONS_DISPATCH_TASKS),
          equipments: hasPermission(
            userPermissions,
            FN_NAMES.FUNCTION_OPERATIONS_DISPATCH_EQUIPMENTS
          ),
          storageBoxes: hasPermission(
            userPermissions,
            FN_NAMES.FUNCTION_OPERATIONS_DISPATCH_STORAGE_BOXES
          ),
        },
      });
    }
  }, [userPermissions]);

  const handlePanelsByPermission = () => {
    let panels: PanelProps[] = [];

    if (isShowAction(currentUser, dispatchPermission.view.day)) {
      panels?.push({
        menuItem: translate("day"),
        key: DiaryType.RESOURCES,
        skipContainerPadding: true,
        flexContainer: true,
        rounded: true,
        render: () => <DiaryResourcesView dataPermission={dispatchPermission.dataSet} />,
      });
    }

    if (isShowAction(currentUser, dispatchPermission.view.week)) {
      panels?.push({
        menuItem: translate("week"),
        key: DiaryType.WEEK,
        skipContainerPadding: true,
        rounded: true,
        render: () => <DiaryWeekView />,
      });
    }

    if (isShowAction(currentUser, dispatchPermission.view.month)) {
      panels?.push({
        menuItem: translate("month"),
        key: DiaryType.MONTH,
        skipContainerPadding: true,
        rounded: true,
        render: () => <DiaryMonthView />,
      });
    }

    if (isShowAction(currentUser, dispatchPermission.view.list)) {
      panels?.push({
        menuItem: translate("list"),
        key: DiaryType.LIST,
        skipContainerPadding: true,
        rounded: true,
        render: () => <DiaryListView />,
      });
    }

    return panels;
  };

  const panels: PanelProps[] = handlePanelsByPermission();

  useDespatchPopupHandlers();

  return (
    <div className={classes.container}>
      <TitlePage
        classNameTitle={classes.title}
        // title={translate("dispatch")}
        // right={isShowAction(currentUser, dispatchPermission.filter) && <DespatchFilters />}
      />

      <div className={classes.content}>
        <UITabMaterial
          onlyRenderTheCurrentActivePanel
          gridClassName={classes.containerTabsGrid}
          className={classes.containerTabs}
          leftPanels={panels}
          leftPanelClassName={classes.leftPanelContainer}
          onChangeLeftPanel={(event, panel, key) => {
            if (key) setViewMode(key as DiaryType);
          }}
          currentActivePanelRef={fullscreenContainerRef}
          selectedLeftPanel={panels.findIndex((panel) => panel.key === viewMode)}
          rightComponent={() => {
            return (
              <DispatchInfoTimezoneWrapper>
                <Box
                  letterSpacing={0.5}
                  fontFamily="Manrope"
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                  gap={1}
                  color={colors.gray500}
                >
                  <IconTimezoneClock color={colors.gray500} />
                  <span>{formatTimezoneDisplay(userTimezone)}</span>
                </Box>
                {viewMode === DiaryType.RESOURCES && (
                  <DispatchInformationTooltip
                    placement="left"
                    title={translate("someChangesMayTakeSomeTimeToBeAppliedOnThisScreen")}
                  >
                    <DispatchInformation>
                      <InformationIcon size={16} />
                    </DispatchInformation>
                  </DispatchInformationTooltip>
                )}
                <DespatchFilters />
              </DispatchInfoTimezoneWrapper>
            );
          }}
        />
      </div>

      <DespatchCreateTask
        ref={createTaskRef}
        onSuccessTaskCreated={(task, taskDTO) => {
          emit(BusEventNames.DESPATCH_ON_SUCCESS_CREATE_TASK, { task, taskDTO });
        }}
      />

      <DespatchCreatePlaceholderTask
        ref={createPlaceholderTaskRef}
        onSuccessTaskCreated={(task) => {
          emit(BusEventNames.DESPATCH_ON_SUCCESS_CREATE_PLACEHOLDER_TASK, { task });
        }}
      />

      <DespatchDeletePlaceholderTask
        ref={deletePlaceholderTaskRef}
        onSuccessDeletePlaceholderTask={(taskId) => {
          emit(BusEventNames.DESPATCH_ON_SUCCESS_DELETE_PLACEHOLDER_TASK, { taskId });
        }}
      />

      {/* <DespatchTimeControl ref={despatchTimeControlRef} /> */}

      <TimeControlAndExpenseModal ref={despatchTimeControlRef} />

      <DespatchCancelTask
        ref={cancelTaskRef}
        onSuccessCancelTask={(task, crew) => {
          emit(BusEventNames.DESPATCH_ON_SUCCESS_CANCEL_TASK, { task, crew });
        }}
      />

      <DespatchTaskCommentsModal
        ref={taskCommentRef}
        onSuccessCreate={(comment, task) => {
          emit(BusEventNames.DESPATCH_ON_SUCCESS_CREATE_COMMENT, {
            task,
            comment: comment as TaskComment,
          });
        }}
        onSuccessDelete={(commentId, task) => {
          emit(BusEventNames.DESPATCH_ON_SUCCESS_DELETE_COMMENT, { task, commentId });
        }}
      />

      <ChangeTimeModal
        ref={changeTimeRef}
        onSuccessTaskUpdated={(task) => {
          emit(BusEventNames.DESPATCH_ON_SUCCESS_UPDATE_TIME, { task });
        }}
      />

      <StorageBoxSingleUseModal
        ref={storageBoxSingleUseRef}
        onSuccessCreate={(storageBox, storageBoxDTO, payload: DespatchCrew) => {
          emit(BusEventNames.DESPATCH_ON_SUCCESS_CREATE_CREATE_SINGLE_USE_BOX, {
            storageBox,
            storageBoxDTO,
            payload,
          });
        }}
      />

      <CreateCrewModal
        ref={createCrewModalRef}
        onSuccessCreate={(crew) => {
          emit(BusEventNames.DESPATCH_ON_SUCCESS_CREATE_CREW, {
            crew,
          });
        }}
      />

      <DiarySeeTasks ref={seeTasksModalRef} viewMode={viewMode} />
    </div>
  );
};

export const DiaryPage = () => {
  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={dispatchTheme}>
        <StylesProvider generateClassName={generateClassName}>
          <OperationDespatchProvider>
            <DiaryComp />
          </OperationDespatchProvider>
        </StylesProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};
